import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import VoilaBoardService from "../../../_services/VoilaBoardService";
import Config from "../../../_config/config";

interface Board {
  id: number;
  boardUUID: string;
  isPublished: number;
  sharePIN: string | null;
  shareExpiryDate: string | null;
  viewCount: number;
}

interface Props {
  show: boolean;
  onHide: () => void;
  board: Board;
  onUpdated: () => void;
}

const ShareSettingsModal: React.FC<Props> = ({ show, onHide, board, onUpdated }) => {
  const [isPublished, setIsPublished] = useState(!!board.isPublished);
  const [sharePIN, setSharePIN] = useState(board.sharePIN || "");
  const [shareExpiryDate, setShareExpiryDate] = useState(
    board.shareExpiryDate ? board.shareExpiryDate.slice(0, 10) : "",
  );
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setIsPublished(!!board.isPublished);
    setSharePIN(board.sharePIN || "");
    setShareExpiryDate(board.shareExpiryDate ? board.shareExpiryDate.slice(0, 10) : "");
  }, [board]);

  const ilearnBase = Config.ilearnUrl.replace(/\/+$/, "");
  const shareUrl = `${ilearnBase}/voila/${board.boardUUID}`;

  const handleSave = async () => {
    setSaving(true);
    try {
      await VoilaBoardService.updateShareSettings(board.id, {
        isPublished,
        sharePIN: sharePIN || null,
        shareExpiryDate: shareExpiryDate ? shareExpiryDate + " 23:59:59" : null,
      });
      onUpdated();
      onHide();
    } catch (err) {
      console.error("Failed to update share settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Share Settings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="publish-switch"
              label="Publish board (enable shareable link)"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
            />
          </Form.Group>

          {isPublished && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>PIN (optional)</Form.Label>
                <Form.Control
                  value={sharePIN}
                  onChange={(e) => setSharePIN(e.target.value)}
                  placeholder="Leave empty for no PIN"
                  maxLength={10}
                />
                <Form.Text className="text-muted">
                  If set, visitors must enter this PIN to view the board.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Expiry Date (optional)</Form.Label>
                <Form.Control
                  type="date"
                  value={shareExpiryDate}
                  onChange={(e) => setShareExpiryDate(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Link will stop working after this date. Leave empty for no expiry.
                </Form.Text>
              </Form.Group>

              <div className="share-link-box">
                <span style={{ flex: 1 }}>{shareUrl}</span>
                <Button variant="primary" size="sm" onClick={copyLink}>
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </div>

              <p className="text-muted mt-2" style={{ fontSize: 12 }}>
                Views: {board.viewCount || 0}
              </p>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareSettingsModal;
